import {getLogger} from './logging'
import {getConfig} from './config'
import {
  DataLayerEvent,
  BaseGuestPortalEvent,
  VisitorData,
  CachedPageInfo
} from './analyticsInterfaces'

const logger = getLogger('analyticsHelpers')

// Storage keys
export const VISITOR_STORAGE_KEY = 'guest_portal_visitor_data'
export const PAGE_SESSION_STORAGE_KEY = 'guest_portal_page_session'
export const USER_JOURNEY_STORAGE_KEY = 'guest_portal_user_journey'
export const PREVIOUS_PAGE_STORAGE_KEY = 'guest_portal_previous_page'

// Cache interfaces
interface CachedStorageData {
  userJourney: any
  pageSession: any
  previousPage: any
  lastUpdated: number
}

// Cache variables
let cachedPageInfo: CachedPageInfo | null = null
let cachedStorageData: CachedStorageData | null = null
const PAGE_INFO_CACHE_TTL = 50 // Cache for 50ms to avoid excessive DOM access
const STORAGE_CACHE_TTL = 50 // Cache for 50ms to avoid excessive storage access

/**
 * Performance-optimized function to get current page information
 * Caches DOM access for a short period to avoid repeated queries
 */
export function getCurrentPageInfo(): CachedPageInfo {
  const now = Date.now()

  // Return cached info if still valid
  if (cachedPageInfo && now - cachedPageInfo.timestamp < PAGE_INFO_CACHE_TTL) {
    return cachedPageInfo
  }

  // Update cache with fresh DOM data
  cachedPageInfo = {
    pathname: window.location.pathname,
    href: window.location.href,
    title: document.title,
    timestamp: now
  }

  return cachedPageInfo
}

/**
 * Force refresh the page info cache (use when page changes are detected)
 */
export function refreshPageInfoCache(): CachedPageInfo {
  const now = Date.now()
  cachedPageInfo = {
    pathname: window.location.pathname,
    href: window.location.href,
    title: document.title,
    timestamp: now
  }
  return cachedPageInfo
}

/**
 * Debounce utility for performance optimization
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }

    timeout = setTimeout(() => {
      func(...args)
    }, wait)
  }
}

/**
 * Performance-optimized sessionStorage access with caching
 */
export function getCachedStorageData(): CachedStorageData {
  const now = Date.now()

  // Return cached data if still valid
  if (
    cachedStorageData &&
    now - cachedStorageData.lastUpdated < STORAGE_CACHE_TTL
  ) {
    return cachedStorageData
  }

  // Update cache with fresh storage data
  cachedStorageData = {
    userJourney: getStorageItem(USER_JOURNEY_STORAGE_KEY),
    pageSession: getStorageItem(PAGE_SESSION_STORAGE_KEY),
    previousPage: getStorageItem(PREVIOUS_PAGE_STORAGE_KEY),
    lastUpdated: now
  }

  return cachedStorageData
}

/**
 * Helper function to safely get and parse storage items
 */
export function getStorageItem(key: string): any | null {
  try {
    const item = sessionStorage.getItem(key)
    return item ? JSON.parse(item) : null
  } catch (error) {
    console.warn(`Failed to parse storage item ${key}:`, error)
    return null
  }
}

/**
 * Helper function to safely set storage items
 */
export function setStorageItem(key: string, value: any): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
    // Invalidate cache when we write to storage
    cachedStorageData = null
  } catch (error) {
    console.warn(`Failed to set storage item ${key}:`, error)
  }
}

/**
 * Force refresh storage cache (use when you know storage has changed externally)
 */
export function refreshStorageCache(): CachedStorageData {
  cachedStorageData = null
  return getCachedStorageData()
}

/**
 * Initializes Google Analytics 4 (GA4) with the provided tracking ID.
 * This function should be called once at the start of your application.
 */
export function initializeGA4(): void {
  const config = getConfig()
  const googleAnalyticsId = config.googleAnalyticsId

  if (!googleAnalyticsId) {
    logger.warn({
      message: 'Google Analytics ID not configured, skipping GA4 initialization'
    })
    return
  }

  if (!window.dataLayer) {
    window.dataLayer = []
  }

  // Load gtag.js script
  if (!document.getElementById('ga4-script')) {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`
    script.id = 'ga4-script'
    document.head.appendChild(script)
  }

  // Define gtag function
  window.gtag = () => {
    window.dataLayer.push(arguments)
  }

  // Initialize GA4
  window.gtag('js', new Date())
  window.gtag('config', googleAnalyticsId)
}

/**
 * Push a single data layer event
 */
export function pushDataLayerEvent(event: DataLayerEvent): void {
  try {
    logger.info({
      message: 'Pushing analytics event to data layer',
      data: {event}
    })

    // Push event to data layer for GTM processing
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(event)
  } catch (error) {
    logger.error({
      message: 'Error pushing analytics event to data layer',
      data: {error, event}
    })
  }
}

/**
 * Generates a unique visitor ID
 */
export function generateVisitorId(): string {
  return (
    'visitor_' +
    Math.random().toString(36).substr(2, 9) +
    '_' +
    Date.now().toString(36)
  )
}

/**
 * Generates a unique session ID
 */
export function generateSessionId(): string {
  return (
    'session_' +
    Math.random().toString(36).substr(2, 9) +
    '_' +
    Date.now().toString(36)
  )
}

/**
 * Gets or creates visitor data from localStorage
 */
export function getOrCreateVisitorData(): VisitorData {
  try {
    const existingData = localStorage.getItem(VISITOR_STORAGE_KEY)
    const now = new Date().toISOString()

    if (existingData) {
      const visitorData: VisitorData = JSON.parse(existingData)

      // Check if session has expired (30 minutes of inactivity)
      const sessionExpiry = 30 * 60 * 1000 // 30 minutes in milliseconds
      const lastActivity = new Date(visitorData.lastVisit).getTime()
      const currentTime = new Date().getTime()

      if (currentTime - lastActivity > sessionExpiry) {
        // Start new session for returning visitor
        visitorData.sessionId = generateSessionId()
        visitorData.sessionStart = now
        visitorData.visitCount += 1
      }

      visitorData.lastVisit = now
      localStorage.setItem(VISITOR_STORAGE_KEY, JSON.stringify(visitorData))
      return visitorData
    } else {
      // New visitor
      const newVisitorData: VisitorData = {
        visitorId: generateVisitorId(),
        firstVisit: now,
        lastVisit: now,
        visitCount: 1,
        sessionId: generateSessionId(),
        sessionStart: now
      }
      localStorage.setItem(VISITOR_STORAGE_KEY, JSON.stringify(newVisitorData))
      return newVisitorData
    }
  } catch (error) {
    logger.error({
      message: 'Error managing visitor data',
      data: {error}
    })
    // Fallback to session-only data
    return {
      visitorId: generateVisitorId(),
      firstVisit: new Date().toISOString(),
      lastVisit: new Date().toISOString(),
      visitCount: 1,
      sessionId: generateSessionId(),
      sessionStart: new Date().toISOString()
    }
  }
}

/**
 * Determines if visitor is unique or returning
 */
export function getVisitorType(
  visitorData: VisitorData
): 'unique' | 'returning' {
  return visitorData.visitCount === 1 ? 'unique' : 'returning'
}

/**
 * Creates standardized base event data that all events should include
 */
export function createBaseEventData(
  userId?: string
): Omit<BaseGuestPortalEvent, 'event'> {
  const pageInfo = getCurrentPageInfo()
  const visitorData = getOrCreateVisitorData()

  return {
    timestamp: new Date().toISOString(),
    session_id: visitorData.sessionId,
    visitor_id: visitorData.visitorId,
    user_id: userId,
    page_path: pageInfo.pathname,
    page_url: pageInfo.href
  }
}

/**
 * Gets current user email if available
 */
export function getCurrentUser(): string | undefined {
  try {
    // Try to get user from token storage
    const userToken = localStorage.getItem('user_token')
    if (userToken) {
      const tokenData = JSON.parse(userToken)
      return tokenData.email
    }

    // Fallback: try to get from user state in sessionStorage
    const userState = sessionStorage.getItem('user_state')
    if (userState) {
      const userData = JSON.parse(userState)
      return userData.email
    }

    return undefined
  } catch (error) {
    console.warn('Could not get current user for analytics:', error)
    return undefined
  }
}

/**
 * Gets current visitor statistics
 */
export function getVisitorStats(): {
  visitorId: string
  visitorType: 'unique' | 'returning'
  visitCount: number
  sessionId: string
} | null {
  try {
    const visitorData = getOrCreateVisitorData()
    return {
      visitorId: visitorData.visitorId,
      visitorType: getVisitorType(visitorData),
      visitCount: visitorData.visitCount,
      sessionId: visitorData.sessionId
    }
  } catch (error) {
    logger.error({
      message: 'Error getting visitor stats',
      data: {error}
    })
    return null
  }
}

/**
 * Starts tracking session duration for a specific page
 */
export function startPageSessionTracking(userId?: string): void {
  try {
    const visitorData = getOrCreateVisitorData()
    const sessionData = {
      pagePath: getCurrentPageInfo().pathname,
      sessionId: visitorData.sessionId,
      userId,
      startTime: Date.now(),
      pageTitle: document.title
    }

    setStorageItem(PAGE_SESSION_STORAGE_KEY, sessionData)

    logger.debug({
      message: 'Started page session tracking',
      data: {sessionData}
    })
  } catch (error) {
    logger.error({
      message: 'Error starting page session tracking',
      data: {error}
    })
  }
}

/**
 * Ends tracking session duration and sends the event
 */
export function endPageSessionTracking(): void {
  try {
    const storageData = getCachedStorageData()
    const sessionData = storageData.pageSession

    if (!sessionData) {
      logger.warn({
        message: 'No active page session to end'
      })
      return
    }

    const endTime = Date.now()
    const durationSeconds = Math.round((endTime - sessionData.startTime) / 1000)

    logger.debug({
      message: 'Ending page session tracking',
      data: {
        pagePath: sessionData.pagePath,
        durationSeconds,
        userId: sessionData.userId
      }
    })

    // Clear the session
    setStorageItem(PAGE_SESSION_STORAGE_KEY, null)
  } catch (error) {
    logger.error({
      message: 'Error ending page session tracking',
      data: {error}
    })
  }
}

/**
 * Gets current page session data
 */
export function getCurrentPageSession(): {
  pagePath: string
  pageTitle: string
  startTime: number
  durationSeconds: number
  userId?: string
} | null {
  try {
    const storageData = getCachedStorageData()
    const sessionData = storageData.pageSession

    if (!sessionData) {
      return null
    }

    const durationSeconds = Math.round(
      (Date.now() - sessionData.startTime) / 1000
    )

    return {
      pagePath: sessionData.pagePath,
      pageTitle: sessionData.pageTitle,
      startTime: sessionData.startTime,
      durationSeconds,
      userId: sessionData.userId
    }
  } catch (error) {
    logger.error({
      message: 'Error getting current page session',
      data: {error}
    })
    return null
  }
}
